/**
 * The bookshelf: books and reading positions, persisted in IndexedDB.
 *
 * Why this exists: an audiobook that forgets where you were is not an
 * audiobook. Before this, picking a file was a one-shot action and closing the
 * tab lost everything. Now the file itself is stored, so a book is added once
 * and reopened from the shelf, and the sentence index is written as it reads so
 * "resume" resumes rather than restarting.
 *
 * The whole file is kept in the store rather than the extracted text: re-parsing
 * is fast, and a stored blob survives format changes to the extractor.
 */

const DB_NAME = 'bishop-bookshelf';
const DB_VERSION = 1;
const BOOKS = 'books';
const PROGRESS = 'progress';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(BOOKS)) {
        db.createObjectStore(BOOKS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PROGRESS)) {
        db.createObjectStore(PROGRESS, { keyPath: 'bookId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Could not open the bookshelf'));
  });
}

function tx(db, store, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/** Stable id from name + size, so re-adding the same file reopens it rather
 *  than creating a duplicate shelf entry. */
export function bookId(file) {
  return `${file.name}::${file.size}`;
}

export async function listBooks() {
  const db = await openDb();
  const [books, progress] = await Promise.all([
    tx(db, BOOKS, 'readonly', (s) => s.getAll()),
    tx(db, PROGRESS, 'readonly', (s) => s.getAll()),
  ]);
  const byId = Object.fromEntries((progress || []).map((p) => [p.bookId, p]));
  return (books || [])
    .map((b) => ({ ...b, progress: byId[b.id] || null }))
    .sort((a, b) => (b.progress?.updatedAt || b.addedAt || 0) - (a.progress?.updatedAt || a.addedAt || 0));
}

export async function addBook(file, meta = {}) {
  const db = await openDb();
  const id = bookId(file);
  const existing = await tx(db, BOOKS, 'readonly', (s) => s.get(id));
  if (existing) return existing;
  const record = {
    id,
    title: meta.title || file.name.replace(/\.(epub|pdf)$/i, ''),
    author: meta.author || '',
    kind: /\.epub$/i.test(file.name) ? 'epub' : 'pdf',
    fileName: file.name,
    size: file.size,
    blob: file,
    addedAt: Date.now(),
  };
  await tx(db, BOOKS, 'readwrite', (s) => s.put(record));
  return record;
}

export async function getBook(id) {
  const db = await openDb();
  return tx(db, BOOKS, 'readonly', (s) => s.get(id));
}

export async function removeBook(id) {
  const db = await openDb();
  await tx(db, BOOKS, 'readwrite', (s) => s.delete(id));
  await tx(db, PROGRESS, 'readwrite', (s) => s.delete(id));
}

export async function saveProgress(id, { index, total, chapter, chapterTitle }) {
  if (!id) return;
  const db = await openDb();
  await tx(db, PROGRESS, 'readwrite', (s) =>
    s.put({ bookId: id, index, total, chapter, chapterTitle, updatedAt: Date.now() }));
}

export async function getProgress(id) {
  if (!id) return null;
  const db = await openDb();
  return tx(db, PROGRESS, 'readonly', (s) => s.get(id));
}

/** Clear the saved position without removing the book. */
export async function resetProgress(id) {
  const db = await openDb();
  await tx(db, PROGRESS, 'readwrite', (s) => s.delete(id));
}
